/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v3";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import * as types from "../../types/primitives.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

/**
 * Side that the column is pinned to. 'left' or 'right', or empty for no pinning. Default is nil.
 */
export const Pinned = {
  Left: "left",
  Right: "right",
} as const;
/**
 * Side that the column is pinned to. 'left' or 'right', or empty for no pinning. Default is nil.
 */
export type Pinned = OpenEnum<typeof Pinned>;

/**
 * The columns of the table.
 */
export type TableColumnConfig = {
  /**
   * Whether the column can be edited.
   */
  canEdit?: boolean | undefined;
  /**
   * Whether the column can be sorted.
   */
  canSort?: boolean | undefined;
  /**
   * An identifier for this column
   */
  columnId: string;
  /**
   * The description of the column
   */
  description?: string | undefined;
  /**
   * The header of the column
   */
  label: string;
  /**
   * Side that the column is pinned to. 'left' or 'right', or empty for no pinning. Default is nil.
   */
  pinned?: Pinned | undefined;
  /**
   * The short label of the column
   */
  shortLabel?: string | undefined;
  /**
   * Whether the column should show an icon.
   */
  showIcon?: boolean | undefined;
  /**
   * The units of the value in the column
   */
  units?: string | undefined;
  /**
   * The width of the column
   */
  width?: string | undefined;
};

/** @internal */
export const Pinned$inboundSchema: z.ZodType<Pinned, z.ZodTypeDef, unknown> =
  openEnums.inboundSchema(Pinned);
/** @internal */
export const Pinned$outboundSchema: z.ZodType<string, z.ZodTypeDef, Pinned> =
  openEnums.outboundSchema(Pinned);

/** @internal */
export const TableColumnConfig$inboundSchema: z.ZodType<
  TableColumnConfig,
  z.ZodTypeDef,
  unknown
> = z.object({
  canEdit: types.boolean().default(false),
  canSort: types.boolean().default(false),
  column_id: types.string(),
  description: types.optional(types.string()),
  label: types.string(),
  pinned: types.optional(Pinned$inboundSchema),
  short_label: types.optional(types.string()),
  showIcon: types.boolean().default(false),
  units: types.optional(types.string()),
  width: types.optional(types.string()),
}).transform((v) => {
  return remap$(v, {
    "column_id": "columnId",
    "short_label": "shortLabel",
  });
});
/** @internal */
export type TableColumnConfig$Outbound = {
  canEdit: boolean;
  canSort: boolean;
  column_id: string;
  description?: string | undefined;
  label: string;
  pinned?: string | undefined;
  short_label?: string | undefined;
  showIcon: boolean;
  units?: string | undefined;
  width?: string | undefined;
};

/** @internal */
export const TableColumnConfig$outboundSchema: z.ZodType<
  TableColumnConfig$Outbound,
  z.ZodTypeDef,
  TableColumnConfig
> = z.object({
  canEdit: z.boolean().default(false),
  canSort: z.boolean().default(false),
  columnId: z.string(),
  description: z.string().optional(),
  label: z.string(),
  pinned: Pinned$outboundSchema.optional(),
  shortLabel: z.string().optional(),
  showIcon: z.boolean().default(false),
  units: z.string().optional(),
  width: z.string().optional(),
}).transform((v) => {
  return remap$(v, {
    columnId: "column_id",
    shortLabel: "short_label",
  });
});

export function tableColumnConfigToJSON(
  tableColumnConfig: TableColumnConfig,
): string {
  return JSON.stringify(
    TableColumnConfig$outboundSchema.parse(tableColumnConfig),
  );
}
export function tableColumnConfigFromJSON(
  jsonString: string,
): SafeParseResult<TableColumnConfig, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => TableColumnConfig$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'TableColumnConfig' from JSON`,
  );
}
